import { ApiError, ValidationError, AuthenticationError } from './api';
import type { UseToastReturn } from '@/hooks/use-toast';
import { translateErrorMessage } from './error-codes';

/**
 * Error types specific to the calendar booking flow
 */
export class ConcurrentBookingError extends Error {
  constructor(message: string = 'This time slot is no longer available') {
    super(message);
    this.name = 'ConcurrentBookingError';
  }
}

export class NetworkTimeoutError extends Error {
  constructor(message: string = 'Request timed out. Please try again.') {
    super(message);
    this.name = 'NetworkTimeoutError';
  }
}

export class ServiceUnavailableError extends Error {
  constructor(message: string = 'Service temporarily unavailable') {
    super(message);
    this.name = 'ServiceUnavailableError';
  }
}

/**
 * Error message mapping for common error scenarios
 */
export const ERROR_MESSAGES = {
  // Booking errors
  CONCURRENT_BOOKING: 'This time slot has already been booked. Please select another time.',
  SLOT_NOT_AVAILABLE: 'This availability slot is no longer available.',
  BOOKING_CONFLICT: 'This booking conflicts with an existing booking.',
  INVALID_TIME_RANGE: 'The booking time must fall within the availability slot.',

  // Network errors
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  TIMEOUT: 'Request timed out. Please try again.',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again later.',

  // Validation errors
  INVALID_PHONE: 'Please enter a valid 10-digit phone number.',
  INVALID_DATE: 'Please select a valid date.',
  INVALID_TIME: 'Please select a valid time.',
  REQUIRED_FIELD: 'This field is required.',

  // Authentication errors
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',

  // Generic errors
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

/**
 * Determine if an error is a concurrent booking conflict
 */
export function isConcurrentBookingError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return (
      error.statusCode === 409 ||
      error.message.toLowerCase().includes('already booked') ||
      error.message.toLowerCase().includes('conflict')
    );
  }
  return false;
}

/**
 * Determine if an error is a network timeout
 */
export function isNetworkTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.name === 'NetworkTimeoutError' ||
      error.message.toLowerCase().includes('timeout') ||
      error.message.toLowerCase().includes('timed out')
    );
  }
  return false;
}

/**
 * Determine if an error is a service unavailable error
 */
export function isServiceUnavailableError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.statusCode === 503 || error.statusCode === 502;
  }
  return false;
}

/**
 * Get user-friendly error message from error object.
 * Error messages from the API client are already translated,
 * but this function also handles domain-specific error types.
 */
export function getErrorMessage(error: unknown): string {
  // Handle concurrent booking errors
  if (isConcurrentBookingError(error)) {
    return ERROR_MESSAGES.CONCURRENT_BOOKING;
  }

  // Handle network timeout errors
  if (isNetworkTimeoutError(error)) {
    return ERROR_MESSAGES.TIMEOUT;
  }

  // Handle service unavailable errors
  if (isServiceUnavailableError(error)) {
    return ERROR_MESSAGES.SERVICE_UNAVAILABLE;
  }

  // Handle API errors (already translated by API client)
  if (error instanceof ApiError) {
    return translateErrorMessage(error.message) || ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  // Handle validation errors
  if (error instanceof ValidationError) {
    return error.message || ERROR_MESSAGES.REQUIRED_FIELD;
  }

  // Handle authentication errors
  if (error instanceof AuthenticationError) {
    return translateErrorMessage(error.message) || ERROR_MESSAGES.SESSION_EXPIRED;
  }

  // Handle generic errors - translate in case they contain error codes
  if (error instanceof Error) {
    return translateErrorMessage(error.message) || ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  return ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Display error toast with appropriate message and retry action
 */
export function handleError(
  error: unknown,
  toast: UseToastReturn,
  options?: {
    title?: string;
    onRetry?: () => void;
    retryLabel?: string;
  }
) {
  const message = getErrorMessage(error);
  const title = options?.title || 'Error';

  toast.error(title, {
    description: message,
    action: options?.onRetry
      ? {
          label: options.retryLabel || 'Retry',
          onClick: options.onRetry,
        }
      : undefined,
  });
}
